/**
 * UpdateManager -- Auto-update system for AWENI.
 *
 * Checks daily for new versions, downloads on WiFi/ethernet,
 * handles force updates, SW lifecycle, and data deltas.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VersionManifest {
  app_version: string;
  sw_version: string;
  min_client_version: string;
  data: Record<string, string>;
  force_update: boolean;
  changelog: string;
  download_url?: string;
}

export interface UpdateState {
  lastCheck: number;
  availableVersion: string | null;
  downloaded: boolean;
  pendingInstall: boolean;
  changelog: string | null;
  forceUpdate: boolean;
  downloadUrl: string | null;
}

type UpdateEventType =
  | 'update-available'
  | 'update-downloaded'
  | 'update-applied'
  | 'force-update-required'
  | 'download-progress';

interface UpdateEventDetail {
  manifest?: VersionManifest;
  progress?: number; // 0-100
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VERSION_ENDPOINT = '/aweni/api/v1/versions';
const STORAGE_KEY = 'aweni_update_state';
const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours
const APP_VERSION = '__APP_VERSION__'; // replaced by Vite at build time

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function compareSemVer(a: string, b: string): number {
  const pa = a.replace(/^v/, '').split('.').map(Number);
  const pb = b.replace(/^v/, '').split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da - db;
  }
  return 0;
}

function getConnectionType(): string {
  const nav = navigator as NavigatorWithConnection;
  return nav.connection?.type ?? 'unknown';
}

function isUnmetered(): boolean {
  const type = getConnectionType();
  return type === 'wifi' || type === 'ethernet';
}

interface NetworkInformation {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
  downlink?: number;
  addEventListener?(type: string, cb: EventListener): void;
  removeEventListener?(type: string, cb: EventListener): void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
}

// ---------------------------------------------------------------------------
// UpdateManager
// ---------------------------------------------------------------------------

export class UpdateManager {
  private state: UpdateState;
  private emitter = new EventTarget();
  private checking = false;

  constructor() {
    this.state = this.loadState();
  }

  // ---- Public API ---------------------------------------------------------

  /**
   * Run on app startup. Checks if an update check is due, respects connection.
   */
  async init(): Promise<void> {
    // Register periodic sync if available
    this.registerPeriodicSync();

    // If a force update was pending, show it immediately
    if (this.state.forceUpdate && this.state.availableVersion) {
      this.emit('force-update-required', { manifest: this.buildPartialManifest() });
      return;
    }

    // Check if we're due for a version check
    const elapsed = Date.now() - this.state.lastCheck;
    if (elapsed >= CHECK_INTERVAL_MS) {
      await this.checkForUpdates();
    }
  }

  /**
   * Explicitly check for updates. Returns the manifest if an update is available.
   */
  async checkForUpdates(): Promise<VersionManifest | null> {
    if (this.checking) return null;
    this.checking = true;

    try {
      const res = await fetch(VERSION_ENDPOINT, { cache: 'no-store' });
      if (!res.ok) return null;

      const manifest: VersionManifest = await res.json();
      this.state.lastCheck = Date.now();

      const localVersion = this.getLocalVersion();
      const hasAppUpdate = compareSemVer(manifest.app_version, localVersion) > 0;
      const isForced =
        manifest.force_update ||
        compareSemVer(manifest.min_client_version, localVersion) > 0;

      if (!hasAppUpdate && !this.hasDataUpdates(manifest)) {
        this.saveState();
        return null;
      }

      this.state.availableVersion = manifest.app_version;
      this.state.changelog = manifest.changelog;
      this.state.forceUpdate = isForced;
      this.state.downloadUrl = manifest.download_url ?? null;
      this.state.downloaded = false;
      this.state.pendingInstall = false;
      this.saveState();

      if (isForced) {
        this.emit('force-update-required', { manifest });
        return manifest;
      }

      this.emit('update-available', { manifest });

      // Auto-download on WiFi/ethernet
      if (isUnmetered()) {
        await this.downloadUpdate(manifest);
      }

      return manifest;
    } catch {
      // Network error — skip silently
      return null;
    } finally {
      this.checking = false;
    }
  }

  /**
   * Apply a pending update (SW skipWaiting + reload, or navigate to store).
   */
  async applyPendingUpdate(): Promise<void> {
    if (this.state.downloadUrl) {
      // Native APK — open store or direct link
      window.open(this.state.downloadUrl, '_blank');
      return;
    }

    // PWA — tell the waiting SW to activate, then reload
    const reg = await navigator.serviceWorker?.getRegistration('/aweni/');
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    this.state.pendingInstall = false;
    this.state.downloaded = false;
    this.state.availableVersion = null;
    this.saveState();

    this.emit('update-applied', {});
    window.location.reload();
  }

  getUpdateState(): Readonly<UpdateState> {
    return { ...this.state };
  }

  isUpdateAvailable(): boolean {
    return this.state.availableVersion !== null;
  }

  /** Subscribe to update events */
  on(event: UpdateEventType, handler: (detail: UpdateEventDetail) => void): void {
    this.emitter.addEventListener(event, ((e: CustomEvent<UpdateEventDetail>) => {
      handler(e.detail);
    }) as EventListener);
  }

  // ---- Download -----------------------------------------------------------

  private async downloadUpdate(manifest: VersionManifest): Promise<void> {
    try {
      // 1. SW update — the browser handles this automatically via registration
      //    We just need to trigger an update check on the registration.
      const reg = await navigator.serviceWorker?.getRegistration('/aweni/');
      if (reg) {
        await reg.update();
      }

      // 2. Data updates — fetch deltas for each changed data source
      const dataKeys = Object.keys(manifest.data);
      let completed = 0;
      for (const key of dataKeys) {
        const localVer = await this.getLocalDataVersion(key);
        if (localVer !== manifest.data[key]) {
          await this.fetchDataDelta(key, localVer, manifest.data[key]);
        }
        completed++;
        this.emit('download-progress', {
          progress: Math.round((completed / dataKeys.length) * 100),
        });
      }

      this.state.downloaded = true;
      this.state.pendingInstall = true;
      this.saveState();
      this.emit('update-downloaded', { manifest });
    } catch {
      // Partial download — will retry on next check
    }
  }

  private async fetchDataDelta(
    key: string,
    fromVersion: string | null,
    toVersion: string,
  ): Promise<void> {
    const params = new URLSearchParams({ key, to: toVersion });
    if (fromVersion) params.set('from', fromVersion);

    const res = await fetch(`/aweni/api/v1/data-delta?${params}`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`Delta fetch failed for ${key}: ${res.status}`);

    const delta = await res.json();

    // Apply delta to IndexedDB via broadcast (OfflineManager listens)
    const bc = new BroadcastChannel('aweni-updates');
    bc.postMessage({ type: 'data-delta', key, version: toVersion, records: delta.records });
    bc.close();

    // Persist version marker
    localStorage.setItem(`aweni_data_v_${key}`, toVersion);
  }

  // ---- Data version helpers -----------------------------------------------

  private hasDataUpdates(manifest: VersionManifest): boolean {
    for (const [key, version] of Object.entries(manifest.data)) {
      const local = localStorage.getItem(`aweni_data_v_${key}`);
      if (local !== version) return true;
    }
    return false;
  }

  private async getLocalDataVersion(key: string): Promise<string | null> {
    return localStorage.getItem(`aweni_data_v_${key}`);
  }

  // ---- Periodic sync ------------------------------------------------------

  private async registerPeriodicSync(): Promise<void> {
    try {
      const reg = await navigator.serviceWorker?.ready;
      if (reg && 'periodicSync' in reg) {
        const ps = reg as ServiceWorkerRegistration & {
          periodicSync: { register(tag: string, opts: { minInterval: number }): Promise<void> };
        };
        await ps.periodicSync.register('daily-update-check', {
          minInterval: CHECK_INTERVAL_MS,
        });
      }
    } catch {
      // periodicSync not supported or permission denied — fallback is init() on app open
    }
  }

  // ---- State persistence --------------------------------------------------

  private loadState(): UpdateState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as UpdateState;
    } catch {
      // Corrupt state — reset
    }
    return {
      lastCheck: 0,
      availableVersion: null,
      downloaded: false,
      pendingInstall: false,
      changelog: null,
      forceUpdate: false,
      downloadUrl: null,
    };
  }

  private saveState(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // Storage full — non-critical
    }
  }

  private getLocalVersion(): string {
    return APP_VERSION;
  }

  private buildPartialManifest(): VersionManifest {
    return {
      app_version: this.state.availableVersion ?? '',
      sw_version: '',
      min_client_version: '',
      data: {},
      force_update: this.state.forceUpdate,
      changelog: this.state.changelog ?? '',
      download_url: this.state.downloadUrl ?? undefined,
    };
  }

  private emit(type: UpdateEventType, detail: UpdateEventDetail): void {
    this.emitter.dispatchEvent(new CustomEvent(type, { detail }));
  }
}

// ---------------------------------------------------------------------------
// UI Components (render into DOM imperatively)
// ---------------------------------------------------------------------------

/**
 * Render a non-intrusive top banner: "New version X available" [Update now] [Later]
 */
export function renderUpdateBanner(
  manager: UpdateManager,
  manifest: VersionManifest,
): HTMLElement {
  const banner = document.createElement('div');
  banner.className = 'aweni-update-banner';
  banner.setAttribute('role', 'alert');
  banner.style.cssText = `
    position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
    background: var(--accent, #1a73e8); color: #fff;
    padding: 10px 16px; display: flex; align-items: center; gap: 12px;
    font-size: 14px; animation: slideDown 300ms ease-out;
  `;

  const msg = document.createElement('span');
  msg.style.cssText = 'flex:1';
  msg.textContent = `New version ${manifest.app_version} available`;

  const updateBtn = document.createElement('button');
  updateBtn.style.cssText =
    'padding:6px 14px;border-radius:6px;background:#fff;color:var(--accent,#1a73e8);font-weight:600;font-size:13px;cursor:pointer;';
  updateBtn.textContent = 'Update now';
  updateBtn.addEventListener('click', () => {
    manager.applyPendingUpdate();
    banner.remove();
  });

  const laterBtn = document.createElement('button');
  laterBtn.style.cssText =
    'padding:6px 10px;border-radius:6px;background:transparent;color:#fff;font-size:13px;cursor:pointer;text-decoration:underline;';
  laterBtn.textContent = 'Later';
  laterBtn.addEventListener('click', () => banner.remove());

  banner.append(msg, updateBtn, laterBtn);
  return banner;
}

/**
 * Render a full-screen force-update blocker.
 */
export function renderForceUpdateScreen(
  manager: UpdateManager,
  manifest: VersionManifest,
): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'aweni-force-update';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 9999;
    background: var(--bg, #fff); display: flex; flex-direction: column;
    align-items: center; justify-content: center; padding: 24px; text-align: center;
  `;

  const icon = document.createElement('div');
  icon.style.cssText = 'font-size:48px;margin-bottom:16px;';
  icon.textContent = '\u{1F504}'; // 🔄

  const title = document.createElement('h2');
  title.style.cssText = 'font-size:22px;font-weight:700;margin-bottom:8px;color:var(--text-primary,#111);';
  title.textContent = 'Update Required';

  const version = document.createElement('p');
  version.style.cssText = 'font-size:14px;color:var(--text-secondary,#666);margin-bottom:16px;';
  version.textContent = `Version ${manifest.app_version}`;

  const changelog = document.createElement('div');
  changelog.style.cssText =
    'max-width:400px;text-align:start;background:var(--card-bg,#f5f5f5);border-radius:12px;padding:16px;margin-bottom:24px;font-size:13px;line-height:1.6;color:var(--text-primary,#333);white-space:pre-line;max-height:200px;overflow-y:auto;';
  changelog.textContent = manifest.changelog || 'Important improvements and bug fixes.';

  const btn = document.createElement('button');
  btn.style.cssText =
    'padding:12px 32px;border-radius:10px;background:var(--accent,#1a73e8);color:#fff;font-size:16px;font-weight:600;cursor:pointer;';
  btn.textContent = 'Update';
  btn.addEventListener('click', () => manager.applyPendingUpdate());

  overlay.append(icon, title, version, changelog, btn);
  return overlay;
}

/**
 * Render a download progress bar (appended inside an existing banner).
 */
export function renderDownloadProgress(container: HTMLElement, progress: number): void {
  let bar = container.querySelector<HTMLDivElement>('.aweni-dl-progress');
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'aweni-dl-progress';
    bar.style.cssText =
      'position:absolute;bottom:0;left:0;height:3px;background:#fff;transition:width 200ms;border-radius:0 0 0 3px;';
    container.style.position = 'relative';
    container.appendChild(bar);
  }
  bar.style.width = `${Math.min(100, Math.max(0, progress))}%`;
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const updateManager = new UpdateManager();
